'use client'

import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { popModal } from '@/components/modals'
import sendToBothAnalytics from '@/lib/mixpanel/sendToBothAnalytics'
import { MIXPANEL_CLIENT_EVENT_NAMES } from '@/lib/mixpanel/constant'
import { useUser } from '@/contexts/user'
import { useLocale, useTranslations } from 'next-intl'
import { Input } from '@/components/ui/input'
import {
  ONBOARDING_SURVEY_KEY,
  type OnboardingQuestionConfig,
} from '@/lib/onboarding/questions'

type OnboardingQuestion = OnboardingQuestionConfig

function DotIndicator({ total, index }: { total: number; index: number }) {
  return (
    <div className="flex items-center justify-center gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          className={cn(
            'h-1.5 w-1.5 rounded-full bg-white/20',
            i === index && 'bg-white',
          )}
        />
      ))}
    </div>
  )
}

function OptionChip({
  selected,
  onClick,
  children,
  className,
}: {
  selected: boolean
  onClick: () => void
  children: React.ReactNode
  className?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-3 py-2 rounded-md text-sm bg-white/5 text-gray-200 border border-white/10 hover:bg-white/10 transition-colors whitespace-nowrap break-words text-wrap',
        selected &&
          'bg-white/15 border-white/20 shadow-[0_0_0_1px_rgba(255,255,255,0.08)_inset]',
        className,
      )}
    >
      {children}
    </button>
  )
}

async function saveBulkAnswers(
  answers: {
    question_id: string
    selected_options: string[]
    custom_input?: string
  }[],
) {
  const res = await fetch('/api/onboarding/answers', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ answers }),
  })
  if (!res.ok) throw new Error('Failed to save onboarding answer')
}

const DEFAULT_SURVEY_KEY = ONBOARDING_SURVEY_KEY

function InnerModal({
  name,
  initialQuestions,
  surveyKey = DEFAULT_SURVEY_KEY,
}: {
  name: 'OnboardingSurveyModal'
  initialQuestions?: OnboardingQuestion[]
  surveyKey?: string
}) {
  const { user } = useUser()
  const locale = useLocale() as string
  const t = useTranslations()
  const [questions, setQuestions] = useState<OnboardingQuestion[]>(
    initialQuestions || [],
  )
  const [stepIndex, setStepIndex] = useState(0)
  const [answerMap, setAnswerMap] = useState<Record<string, string[]>>({})
  // Per-question custom input (used when selecting "Other")
  const [customInputMap, setCustomInputMap] = useState<Record<string, string>>(
    {},
  )
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const step = questions[stepIndex]
  const completedKey = `onboarding_completed_${surveyKey}`
  const dismissedKey = `onboarding_dismissed_${surveyKey}`
  const showBackButton = !submitted && stepIndex > 0

  useEffect(() => {
    if (initialQuestions && initialQuestions.length > 0) {
      setQuestions(initialQuestions)
    }
    let aborted = false
    if (!initialQuestions || initialQuestions.length === 0) {
      ;(async () => {
        try {
          const res = await fetch(
            `/api/onboarding/questions?survey=${encodeURIComponent(surveyKey)}`,
            {
              cache: 'no-store',
            },
          )
          if (!res.ok) return
          const data = (await res.json()) as { questions: OnboardingQuestion[] }
          if (!aborted) setQuestions(data.questions || [])
        } catch {
          if (!aborted) setQuestions([])
        }
      })()
    }
    return () => {
      aborted = true
    }
  }, [initialQuestions, surveyKey])

  const isOtherSelectedForStep = useMemo(() => {
    if (!step || !step.has_custom_input) return false
    const answers = answerMap[step._id] || []
    return answers.includes('other')
  }, [answerMap, step])

  const canContinue = useMemo(() => {
    if (!step) return false
    const answers = answerMap[step._id] || []
    if (answers.length === 0) return false
    // If user selected Other, require custom input
    if (step.has_custom_input && isOtherSelectedForStep) {
      const v = (customInputMap[step._id] || '').trim()
      return v.length > 0
    }
    return true
  }, [answerMap, step, isOtherSelectedForStep, customInputMap])

  const handleSelect = (optionKey: string) => {
    setAnswerMap((prev) => {
      if (!step) return prev
      const prevAnswers = prev[step._id] || []
      const exists = prevAnswers.includes(optionKey)
      const nextAnswers =
        step.type === 'multiple'
          ? exists
            ? prevAnswers.filter((x) => x !== optionKey)
            : [...prevAnswers, optionKey]
          : [optionKey]
      if (step.type === 'multiple') {
        return { ...prev, [step._id]: nextAnswers }
      }
      return { ...prev, [step._id]: nextAnswers }
    })
  }

  const handleSubmit = async () => {
    try {
      setSubmitting(true)
      const answers = questions.map((q) => {
        const selectedKeys = answerMap[q._id] || []
        const isOtherSelected = () =>
          q.has_custom_input && selectedKeys.includes('other')
        const selectedOptions = selectedKeys
          .map((key) => {
            const translationKey = q.optionKeys[key]
            if (!translationKey) return key
            const label = t(translationKey)
            return typeof label === 'string' && label.trim().length
              ? label
              : key
          })
          .filter((value) => typeof value === 'string' && value.length > 0)
        return {
          question_id: q._id,
          selected_options: selectedOptions,
          selected_option_keys: selectedKeys,
          custom_input:
            q.has_custom_input && isOtherSelected()
              ? (customInputMap[q._id] || '').trim()
              : undefined,
        }
      })

      sendToBothAnalytics(
        MIXPANEL_CLIENT_EVENT_NAMES.SUBMIT_ONBOARDING_SURVEY,
        { answers },
        user?._id ? String(user._id) : undefined,
      )

      await saveBulkAnswers(answers)

      try {
        localStorage.setItem('onboarding_completed', '1')
        localStorage.setItem(completedKey, '1')
        await fetch('/api/onboarding/complete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ keys: questions.map((q) => q._id) }),
        })
      } catch {}

      setSubmitted(true)
    } catch (e) {
      // noop – keep modal open; optionally show a toast in future
    } finally {
      setSubmitting(false)
    }
  }

  const handleContinue = async () => {
    const last = stepIndex >= questions.length - 1
    if (last) {
      await handleSubmit()
    } else {
      setStepIndex((i) => i + 1)
    }
  }

  const handleBack = () => {
    if (submitted) return
    setStepIndex((i) => Math.max(i - 1, 0))
  }

  const handleOpenChange = async (open: boolean) => {
    if (!open) {
      if (!submitted) {
        // user dismissed without submitting
        try {
          localStorage.setItem('onboarding_dismissed', '1')
          localStorage.setItem(dismissedKey, '1')
        } catch {}
        // mark server-side dismissed for cross-device gating
        try {
          await fetch('/api/onboarding/dismiss', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ keys: questions.map((q) => q._id) }),
          })
        } catch {}
        // track dismiss event
        try {
          sendToBothAnalytics(
            MIXPANEL_CLIENT_EVENT_NAMES.DISMISS_ONBOARDING_SURVEY,
            {},
            user?._id ? String(user._id) : undefined,
          )
        } catch {}
      }
      popModal(name)
    }
  }

  return (
    <Dialog open={true} onOpenChange={handleOpenChange}>
      <DialogContent
        className="
            w-[800px] max-w-[92vw] p-0 overflow-hidden
            bg-gradient-to-[104deg] from-[#222222] via-[#2B2B2B] to-[#010101]
                    border-white/10 backdrop-blur-[37px] max-h-[90vh] overflow-y-auto
                  "
        onInteractOutside={(e) => {
          e.preventDefault()
        }}
      >
        <div className="relative flex flex-col h-full">
          {/* Header - Fixed */}
          <div className="flex-shrink-0 p-6 pb-4">
            {' '}
            {!step ? (
              <DialogTitle className="text-white font-semibold text-lg">
                Loading…
              </DialogTitle>
            ) : submitted ? (
              <>
                <DialogTitle className="text-white font-semibold text-lg">
                  {t('onboarding.submitted.title')}
                </DialogTitle>
                <p className="text-gray-300 mt-3 text-sm">
                  {t('onboarding.submitted.description')}
                </p>
              </>
            ) : (
              <DialogTitle className="text-white font-semibold text-lg">
                {t(step.titleKey)}
              </DialogTitle>
            )}
          </div>

          {/* Scrollable Content Area */}
          {step && !submitted && (
            <div className="flex-1 px-6 overflow-y-auto max-h-[60vh]">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.entries(step.optionKeys).map(
                  ([key, valueKey], index, array) => {
                    const label = t(valueKey)
                    const selected = (answerMap[step._id] || []).includes(key)
                    const isLastItem = index === array.length - 1
                    const itemsInLastRow = array.length % 4
                    const shouldSpanFull = isLastItem && itemsInLastRow === 1

                    return (
                      <OptionChip
                        key={key}
                        selected={selected}
                        onClick={() => handleSelect(key)}
                        className={cn(shouldSpanFull && 'sm:col-span-4')}
                      >
                        {label}
                      </OptionChip>
                    )
                  },
                )}
              </div>
              {isOtherSelectedForStep && (
                <div className="mt-4">
                  <div className="text-sm text-gray-300 mb-2">
                    {t('onboarding.custom_input.label')}
                  </div>
                  <Input
                    value={customInputMap[step._id] || ''}
                    onChange={(e) =>
                      setCustomInputMap((prev) => ({
                        ...prev,
                        [step._id]: e.target.value,
                      }))
                    }
                    placeholder={t('onboarding.custom_input.placeholder')}
                  />
                  <div className="mt-1 text-xs text-gray-400">
                    {t('onboarding.custom_input.required_message')}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Footer - Fixed */}
          <div className="flex-shrink-0 p-6 pt-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                {showBackButton && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleBack}
                    disabled={submitting}
                  >
                    {t('onboarding.cta.back')}
                  </Button>
                )}
              </div>
              <div className="flex justify-center min-w-[80px]">
                {questions.length > 1 && !submitted && step ? (
                  <DotIndicator total={questions.length} index={stepIndex} />
                ) : null}
              </div>
              <div className="flex-1 flex justify-end">
                <Button
                  onClick={
                    !step
                      ? () => popModal(name)
                      : submitted
                        ? () => popModal(name)
                        : handleContinue
                  }
                  disabled={step && !submitted && (!canContinue || submitting)}
                  variant="light"
                  className="min-w-28"
                >
                  {!step
                    ? t('onboarding.submitted.close_button')
                    : submitted
                      ? t('onboarding.submitted.close_button')
                      : submitting
                        ? t('onboarding.cta.saving')
                        : stepIndex >= questions.length - 1
                          ? t('onboarding.cta.finish')
                          : t('onboarding.cta.continue')}
                </Button>{' '}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
export default function OnboardingSurveyModal({
  name,
  initialQuestions,
  surveyKey = DEFAULT_SURVEY_KEY,
}: {
  name: 'OnboardingSurveyModal'
  initialQuestions?: OnboardingQuestion[]
  surveyKey?: string
}) {
  const { user } = useUser()
  useEffect(() => {
    // Track view event (Mixpanel only; GA handled by server if configured)
    sendToBothAnalytics(
      MIXPANEL_CLIENT_EVENT_NAMES.VIEW_ONBOARDING_SURVEY_MODAL,
      {},
      user?._id ? String(user._id) : undefined,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return (
    <InnerModal
      name={name}
      initialQuestions={initialQuestions}
      surveyKey={surveyKey}
    />
  )
}
